import { createPortal } from "react-dom";
import { X, CreditCard, Lock } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

interface StripePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
  userId: number;
  startDate: Date;
  endDate: Date;
  totalPrice: number;
  workspaceTitle: string;
  message?: string;
}

export default function StripePaymentModal({
  isOpen,
  onClose,
  workspaceId,
  userId,
  startDate,
  endDate,
  totalPrice,
  workspaceTitle,
  message,
}: StripePaymentModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const formatPrice = (amount: number) => {
    return amount.toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");

      const checkoutRequest = {
        UserId: userId,
        WorkspaceId: workspaceId,
        StartDate: startDate.toISOString(),
        EndDate: endDate.toISOString(),
        TotalPrice: totalPrice,
        Message: message || `Instant booking from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`,
      };

      const response = await axios.post(
        "/api/Payment/create-checkout-session",
        checkoutRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success && response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        setError(response.data.message || "Failed to create checkout session");
      }
    } catch (err: unknown) {
      console.error("Error creating checkout session:", err);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ||
            "An error occurred while processing your payment. Please try again."
        );
      } else {
        setError("An error occurred while processing your payment. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200"
            disabled={isProcessing}
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center">
            Instant Booking
          </h2>
          <p className="text-blue-100 text-center text-sm mt-1">
            Secure payment with Stripe
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Workspace Info */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">{workspaceTitle}</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Check-in:</span>
                <span className="font-medium">{startDate.toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Check-out:</span>
                <span className="font-medium">{endDate.toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Payment Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${formatPrice(totalPrice)} AUD</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="font-semibold text-gray-800">Total:</span>
                <span className="font-bold text-blue-600 text-lg">
                  ${formatPrice(totalPrice)} AUD
                </span>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 rounded-xl p-4 mb-6 flex items-start">
            <Lock className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">
                Secure Payment
              </p>
              <p className="text-xs text-blue-700">
                Your payment information is encrypted and secure. You'll be redirected
                to Stripe's secure checkout page.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 disabled:from-gray-400 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 px-4 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg disabled:transform-none disabled:shadow-none"
            >
              {isProcessing ? "Processing..." : "Continue to Payment"}
            </button>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="w-full bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200"
            >
              Cancel
            </button>
          </div>

          {/* Fine Print */}
          <p className="text-xs text-gray-500 text-center mt-4">
            By continuing, you agree to be charged the total amount shown. You will
            receive a confirmation email after successful payment.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
